import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "risa-oasis-ui_v2";
import { getFileDownloadUrl } from "../../../../api/firebase/firestoreService";
import { DetailsCardData } from "../../../../components/detailsCard/detailsCard";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../context/payerNameContextFromSingleOrderLayout";
import { SubmissionStatusEnum } from "../../../../data-model/submissionDocs";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { getAllQuestionsForOrder } from "../../../../redux/slice/formQuestions-slice";
import { getMedicalPaOrderDetailsFor } from "../../../../redux/slice/medicalPa/singleMedicalPaOrder";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { InfoIcon } from "../../../../svg/info-icon";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { capitalizeString } from "../../../../utils/stringModifications";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import { useRefreshFaxStatus } from "../../hooks/useRefreshFaxStatus";
import AuthInfoComponent from "../authInfoComponent/authInfoComponent";
import CptComponentCodes from "../cptCodesComponent/cptComponentCodes";
import StatusComponent from "../statusComponent/statusComponent";

interface SubmissionStatusMainProps {
  id: string;
}

const SubmissionStatusMain = (props: SubmissionStatusMainProps) => {
  const location = useLocation();
  const { isFaxSubmitted } = location.state || {};
  const dispatch = useDispatch<AppDispatch>();
  const [authInfoData, setAuthInfoData] = React.useState<DetailsCardData[]>([]);
  const [donwloadUrl, setDonwloadUrl] = React.useState<string>("");
  const { singleMedicalPaOrder, isLoading } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const orgId = getOrgIdForFetchExternalWorklist();

  const { refreshFaxStatus, faxStatusState } = useRefreshFaxStatus({
    orderId: props.id,
  });

  const hasLoggedToBigQuery = useRef(false);

  React.useEffect(() => {
    if (props.id) {
      dispatch(getMedicalPaOrderDetailsFor(props.id));
      dispatch(getAllQuestionsForOrder(props.id));
    }
  }, [dispatch, props.id]);

  React.useEffect(() => {
    if (singleMedicalPaOrder) {
      if (!hasLoggedToBigQuery.current) {
        hasLoggedToBigQuery.current = true;
        apiWorkerManager.enqueueAndProcess({
          patient_id: singleMedicalPaOrder?.patient_id ?? "",
          order_id: singleMedicalPaOrder?.order_id ?? "",
          org_id: orgId ?? "",
          event_name: EventsName.SUBMISSION_STATUS_SCREEN_SHOWN,
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          additional_data: {
            screen_name: ScreenNamesForBigQuery.SUBMISSION_STATUS_SCREEN,
            source: ScreenNamesForBigQuery.SUBMISSION_STATUS_SCREEN,
            payer_name: payerName,
            regimen_name: singleMedicalPaOrder?.payload?.regimen_name ?? "",
          },
        });
      }

      const dataForAuthComponent: DetailsCardData[] = [
        {
          header: "Full Name",
          text: capitalizeString(
            generateFullName(
              singleMedicalPaOrder?.demographics?.first_name ?? "",
              "",
              singleMedicalPaOrder?.demographics?.last_name ?? "",
            ),
          ),
        },
        {
          header: "Provider Name",
          text: singleMedicalPaOrder?.payload?.practitioner_name ?? "",
        },
        {
          header: "Plan Name",
          text: singleMedicalPaOrder?.coverage?.primary?.payer_name ?? "",
        },
        {
          header: "Member ID",
          text: singleMedicalPaOrder?.coverage?.primary?.member_id ?? "",
        },
        {
          header: "Authorization ID",
          text: "--",
        },
      ];
      setAuthInfoData(dataForAuthComponent);
    }
  }, [singleMedicalPaOrder]);

  React.useEffect(() => {
    if (singleMedicalPaOrder) {
      const downloadUrl = singleMedicalPaOrder?.rpa_screenshot_path ?? "";
      getFileDownloadUrl(downloadUrl ?? "").then((url) => {
        setDonwloadUrl(url);
      });
    }
  }, [singleMedicalPaOrder]);

  const checkIfSubmisionStatusIsAvailable = () => {
    if (isLoading) {
      return <LoaderMessage message="Loading submission status..." />;
    }

    if (!singleMedicalPaOrder?.status?.submission) {
      return <SubmissionDocErrorComponent />;
    }

    return (
      <>
        <div className="submission-status--header">
          <div className="submission-status-form__body--header mb-4 flex justify-between">
            <div className="submission-status-form__body--header-details flex items-center gap-2">
              <div className="submission-status-form__body-patient-name text-h16 font-semibold leading-6 text-primaryGray-1">
                {generateFullName(
                  singleMedicalPaOrder?.demographics?.first_name ?? "",
                  "",
                  singleMedicalPaOrder?.demographics?.last_name ?? "",
                )}
              </div>
              <div className="submission-status-form__body-page-name text-sm font-semiBold text-black">
                ({singleMedicalPaOrder?.demographics?.patient_mrn ?? ""})
              </div>
            </div>
            {singleMedicalPaOrder?.status?.fax_submission_status && (
              <div className="submission-auth-info--header flex justify-end">
                <Button
                  buttonType="secondary"
                  size="medium"
                  disabled={faxStatusState.loading ?? false}
                  onClick={refreshFaxStatus}
                >
                  {faxStatusState.loading ? "Refreshing..." : "Refresh Status"}
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="submission-status--body felx mx-4 my-4 flex-1 flex-col gap-4 overflow-auto">
          <div className="submission-status--current-status flex justify-center">
            {isFaxSubmitted ? (
              <StatusComponent
                status={
                  singleMedicalPaOrder?.status
                    ?.fax_submission_status as SubmissionStatusEnum
                }
              />
            ) : (
              <StatusComponent
                status={
                  singleMedicalPaOrder?.status
                    ?.submission as SubmissionStatusEnum
                }
              />
            )}
          </div>
          <div className="submission-status--auth-info mt-8">
            <AuthInfoComponent
              serviceReviewInfo={authInfoData}
              docDownloadUrl={donwloadUrl}
              submissionStatus={
                singleMedicalPaOrder?.status?.submission as SubmissionStatusEnum
              }
            />
          </div>
          <div className="submission-status--cpt-codes mt-8">
            <CptComponentCodes cptCodesInfo={singleMedicalPaOrder} />
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="submission-status--container flex h-full w-full flex-col overflow-hidden p-4 pb-0">
      {checkIfSubmisionStatusIsAvailable()}
    </div>
  );
};

const SubmissionDocErrorComponent = () => {
  const navigate = useNavigate();
  return (
    <div className="error-container flex h-full w-full flex-col items-center justify-center gap-2">
      <div className="error-icon">
        <InfoIcon height={"48"} width={"48"} stroke={"#0056D6"} />
      </div>
      <div className="error-message text-center text-h11 font-semiBold text-primaryGray-1">
        Please wait while we retrieve the submission status. <br />
        You may revisit this page after a few minutes for the updated status.
      </div>
      <div className="error-message button mt-4">
        <Button
          disabled={false}
          onClick={() => navigate("/worklists")}
          buttonType={"primary"}
          size={"medium"}
        >
          Back to Home page
        </Button>
      </div>
    </div>
  );
};

export default SubmissionStatusMain;
